import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { CircleNotch, X } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AssetViewerPane,
  type AssetFile,
  type AssetViewerPaneHandle,
  CommentPanel,
} from "./AssetViewerPane";

import { useTicketViewData } from "../hooks/useTicketView";

interface CampaignReviewPaneProps {
  /** The review ticket to display (artwork + AI comments + context). */
  ticketId: string;
  /** Close the review screen and return to the default project view. */
  onClose: () => void;
}

/**
 * The project review screen: artwork in the middle with AI comment pins, and
 * the Comments + Context surfaces as standalone "bubble" cards stacked on the
 * right (instead of the docked carousel `AssetViewerPane` renders by default).
 *
 * Selection state (active comment + citation highlight) mirrors
 * `TicketViewPane`, but the layout is intentionally different: the project
 * workspace keeps the feed/chat visible on the left, so this pane owns only
 * the artwork + right-rail bubbles.
 */
export function CampaignReviewPane({
  ticketId,
  onClose,
}: CampaignReviewPaneProps) {
  const {
    ticket,
    comments,
    isLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(ticketId);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextPaneRef = useRef<AssetViewerPaneHandle>(null);

  // Defense-in-depth: never render pins for deleted comments even if a stale
  // one slips through a poll race (mirrors TicketViewPane).
  const activeComments = useMemo(
    () => (comments ?? []).filter((c) => !c.isDeleted),
    [comments],
  );

  // Stable asset identity so AssetViewerPane's memo doesn't thrash on polls.
  const assets: AssetFile[] = useMemo(
    () => latestReviewFiles,
    [latestReviewFiles],
  );

  // Reset selection when switching to a different ticket. Declared BEFORE the
  // auto-select effect so that on mount the null-reset runs first and the
  // auto-select below still lands on the first comment (effect order matters).
  useEffect(() => {
    setSelectedCommentId(null);
    setHighlightPing(null);
  }, [ticketId]);

  // Auto-select the first comment so the artwork lands on a pin and the
  // comments bubble shows real content immediately.
  useEffect(() => {
    if (!selectedCommentId && activeComments.length) {
      setSelectedCommentId(activeComments[0]._id ?? null);
    }
  }, [selectedCommentId, activeComments]);

  useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    },
    [],
  );

  const handleCommentClick = useCallback((comment: BaseTicketComment) => {
    setSelectedCommentId(comment._id ?? null);
    if (comment.rel_x != null && comment.rel_y != null) {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      setHighlightPing({
        x: comment.rel_x,
        y: comment.rel_y,
        page: comment.page_num,
      });
      focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
    }
  }, []);

  // Citation links inside the comment bubble jump the context document to the
  // referenced page (the context viewer is the source spec / Ficha Técnica).
  const handleCitationClick = useCallback((pageNumber: number) => {
    contextPaneRef.current?.navigateToPage(pageNumber);
  }, []);

  return (
    <section
      className="flex h-full min-w-0 flex-1"
      data-testid="campaign-review-pane"
      aria-label="Asset review"
    >
      {/* Artwork (center) */}
      <div className="flex min-w-0 flex-1 flex-col bg-base-white">
        <div
          className="flex h-12 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4"
          data-testid="campaign-review-artwork-header"
        >
          <Text
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
          >
            {ticket?.title ?? "Artwork"}
          </Text>
          <span
            className="rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11"
            aria-hidden="true"
          >
            Artwork
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close review"
            data-testid="campaign-review-close"
            className="flex size-8 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1">
          {isLoading ? (
            <div
              className="flex h-full items-center justify-center"
              data-testid="campaign-review-loading"
            >
              <CircleNotch
                size={24}
                weight="bold"
                className="animate-spin text-puntt-neutral-gray-9"
              />
            </div>
          ) : (
            <AssetViewerPane
              key={ticketId}
              title=""
              assets={assets}
              comments={activeComments}
              activeCommentId={selectedCommentId}
              onCommentClick={handleCommentClick}
              onPinClick={handleCommentClick}
              highlightPosition={highlightPing}
              ticketId={ticketId}
              hideCommentPanel
            />
          )}
        </div>
      </div>

      {/* Right rail: Comments + Context bubbles */}
      <aside className="flex w-[360px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 p-3">
        <div
          className="overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white shadow-sm"
          data-testid="campaign-review-comments-bubble"
          aria-label="Comments"
          role="region"
        >
          {activeComments.length > 0 ? (
            <CommentPanel
              comments={activeComments}
              activeCommentId={selectedCommentId}
              onCommentClick={handleCommentClick}
              onCitationClick={handleCitationClick}
              ticketId={ticketId}
            />
          ) : (
            <p
              className="p-4 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid="campaign-review-comments-empty"
            >
              No comments on this review yet.
            </p>
          )}
        </div>

        {hasContextAssets && (
          <div
            className="flex h-[360px] flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white shadow-sm"
            data-testid="campaign-review-context-bubble"
            aria-label="Context"
            role="region"
          >
            <div className="flex h-9 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-3">
              <h3 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
                Context
              </h3>
              <span
                className="rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[10px] font-medium text-puntt-neutral-gray-11"
                aria-hidden="true"
              >
                Docked
              </span>
            </div>
            <div className="min-h-0 flex-1">
              <AssetViewerPane
                ref={contextPaneRef}
                key={`${ticketId}-context`}
                title=""
                assets={contextFiles}
                hideCommentPanel
              />
            </div>
          </div>
        )}
      </aside>
    </section>
  );
}
